import isEqual from 'lodash/isEqual';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeviceFault } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultForm';
import {
  ColDef,
  CsvExportParams,
  DataTypeDefinition,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
  RowStyle,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { nanoid } from 'nanoid';
import { Flex, Group, Stack, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCopy,
  IconFileExport,
  IconLock,
  IconLockOpen,
  IconPlus,
  IconTableImport,
  IconTrashX,
  IconX,
} from '@tabler/icons-react';
import { useAcknowledge, useConfirm } from 'components/modals/message/MessageProvider';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import ImportMeasurementsHint from 'components/forms/revision/fill-out/modals/device-measurement-modal/import/ImportMeasurementsHint';
import { writeFile } from 'xlsx';
import ImportMeasurementsModal, {
  ImportMeasurementsCallback,
} from 'components/forms/revision/fill-out/modals/device-measurement-modal/import/ImportMeasurementsModal';
import { AgGridReact } from 'ag-grid-react';
import { useDisclosure } from '@mantine/hooks';
import P1Medium from 'components/typography/P1Medium';
import ExportModal from 'components/tables/ExportModal';
import TableWrapper from 'components/tables/TableWrapper';
import ButtonWithConfirm from 'components/modals/confirm/ButtonWithConfirm';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { IFormInputSpec } from 'pages/revisions-module/template-editor/editors/form/types';
import defineColumn from 'components/forms/revision/fill-out/modals/device-measurement-modal/ag-grid/columns/define-column';
import { showNotification } from '@mantine/notifications';
import { ERROR_NOTIFICATION_COLOR } from 'utils/constants';
import { useDeviceFault } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultProvider';
import {
  normalizeMeasurementRowLockMetadata,
  resetMeasurementRowLockMetadata,
  setMeasurementRowLocked,
} from 'components/forms/revision/fill-out/modals/device-measurement-modal/measurement-row-lock';
import type { MeasurementRowLockMetadata } from 'components/forms/revision/fill-out/modals/device-measurement-modal/measurement-row-lock';
import buildMeasurementWorkbook from 'components/forms/revision/fill-out/modals/device-measurement-modal/ag-grid/measurement-xlsx-export';
import {
  parseMeasurementWorkbook,
  readMeasurementWorkbook,
} from 'components/forms/revision/fill-out/modals/device-measurement-modal/ag-grid/measurement-xlsx-import';

type TableMeasurement<TMeasurement> = TMeasurement & {
  _id: string;
  _faults: DeviceFault[];
} & MeasurementRowLockMetadata;

type MeasurementTableMode = 'edit' | 'view';

export interface DeviceMeasurementFormData<TMeasurement> {
  measurements: TableMeasurement<TMeasurement>[];
}

export interface DeviceMeasurementFormProps<TMeasurement> {
  deviceTypeId: number;
  revisionKind: 'revision' | 'form';
  spec?: Readonly<IFormInputSpec[]>;
  lockingEnabled?: boolean;
  hideFaults?: boolean;
  mode?: MeasurementTableMode;
  initialValues?: DeviceMeasurementFormData<TMeasurement>;
  search?: string;
  onSubmit?: (values: DeviceMeasurementFormData<TMeasurement>) => void;
  onClose?: () => void;
}

/**
 * Displays actions that can be done with the measurement row.
 */
function ActionsColumn<TMeasurement>({
  node,
  hideFaults,
  lockingEnabled,
  onDelete,
  onToggleLock,
  syncRows,
  context: { deviceTypeId },
}: {
  node: IRowNode<TableMeasurement<TMeasurement>>;
  hideFaults: boolean;
  lockingEnabled: boolean;
  onDelete: (index: number) => void;
  onToggleLock: (index: number, locked: boolean) => void;
  syncRows: () => void;
  context: { deviceTypeId: number };
}) {
  const { specifyFaults } = useDeviceFault();
  const isLocked = node?.data?._locked ?? false;
  const isEffectivelyLocked = lockingEnabled && isLocked;

  /** Handles the addition of a fault. */
  const handleAddFault = (): void => {
    if (isEffectivelyLocked) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    specifyFaults({
      title: 'Nedostatky - Prívod',
      deviceTypeId,
      initialValues: { faults: node?.data?._faults ?? [] },
      onSubmit: ({ faults }) => {
        node.setData(normalizeMeasurementRowLockMetadata({ ...node.data!, _faults: faults }));
        syncRows();
      },
    });
  };

  return (
    <Group spacing={8} noWrap>
      {!hideFaults && (
        <Button
          w={124}
          px={0}
          size="sm"
          variant="secondary"
          leftIcon={<IconAlertTriangle />}
          onClick={handleAddFault}
          disabled={isEffectivelyLocked}
        >
          Nedostatky ({node?.data?._faults.length ?? 0})
        </Button>
      )}
      {lockingEnabled && (
        <Tooltip
          withinPortal
          label={isLocked ? 'Odomknúť meranie' : 'Uzamknúť meranie'}
          openDelay={200}
          withArrow={false}
          offset={4}
        >
          <ActionIcon
            variant={isLocked ? 'primary' : 'subtle-gray'}
            size="sm"
            p={6}
            onClick={() => onToggleLock(node.rowIndex!, !isLocked)}
          >
            {isLocked ? <IconLockOpen /> : <IconLock />}
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip withinPortal label="Zmazať meranie" openDelay={200} withArrow={false} offset={4}>
        <ActionIcon
          variant="danger-secondary"
          size="sm"
          p={6}
          onClick={() => onDelete(node.rowIndex!)}
          disabled={isEffectivelyLocked}
        >
          <IconTrashX />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

/**
 * Compares two row snapshots to avoid writing an unchanged dataset back into React state.
 */
function areMeasurementRowsEqual<TMeasurement>(
  previousRows: TableMeasurement<TMeasurement>[],
  nextRows: TableMeasurement<TMeasurement>[]
): boolean {
  return isEqual(previousRows, nextRows);
}

/**
 * Returns the most recent lock history event for a measurement row.
 */
function getLatestMeasurementLockEvent<TMeasurement>(row: TableMeasurement<TMeasurement>) {
  return row._lockHistory.length > 0 ? row._lockHistory[row._lockHistory.length - 1] : undefined;
}

/**
 * Formats a lock history timestamp for display.
 */
function formatMeasurementLockTimestamp(timestamp?: string): string {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('sk-SK');
}

/**
 * Returns a human-readable lock status label.
 */
function getMeasurementLockStatusLabel<TMeasurement>(row: TableMeasurement<TMeasurement>): string {
  return row._locked ? 'Uzamknuté' : '';
}

/**
 * Returns the formatted timestamp of the most recent lock state change.
 */
function getMeasurementLastLockUpdateLabel<TMeasurement>(row: TableMeasurement<TMeasurement>): string {
  return formatMeasurementLockTimestamp(getLatestMeasurementLockEvent(row)?.timestamp);
}

/**
 * Formats the full lock history for display inside a tooltip.
 */
function getMeasurementLockHistoryLines<TMeasurement>(row: TableMeasurement<TMeasurement>): string[] {
  return [...row._lockHistory]
    .reverse()
    .map(
      ({ action, timestamp }) =>
        `${formatMeasurementLockTimestamp(timestamp)} - ${action === 'lock' ? 'Uzamknutie' : 'Odomknutie'}`
    );
}

/**
 * Form for collecting device measurement data.
 */
export default function AgGridDeviceMeasurementTable<TMeasurement>({
  deviceTypeId,
  revisionKind,
  spec = [],
  lockingEnabled = false,
  hideFaults = false,
  mode = 'edit',
  initialValues = { measurements: [] },
  search = '',
  onSubmit = noop,
  onClose = noop,
}: DeviceMeasurementFormProps<TMeasurement>) {
  const { confirm } = useConfirm();
  const { acknowledge } = useAcknowledge();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const ref = useRef<AgGridReact>(null);
  const normalizedInitialMeasurements = useMemo(
    () => initialValues.measurements.map((row) => normalizeMeasurementRowLockMetadata(row)),
    [initialValues.measurements]
  );

  const [selectedRows, setSelectedRows] = useState<TableMeasurement<TMeasurement>[]>([]);
  const [rowsSnapshot, setRowsSnapshot] = useState<TableMeasurement<TMeasurement>[]>(normalizedInitialMeasurements);
  const [exporting, { open: startExporting, close: stopExporting }] = useDisclosure(false);
  const [importing, { open: startImporting, close: stopImporting }] = useDisclosure(false);

  const isViewMode = mode === 'view';
  const requiredColumns = useMemo(() => spec.filter((s) => s.required).map((s) => s.name), [spec]);
  const gridContext = useMemo(() => ({ deviceTypeId }), [deviceTypeId]);
  const lockedRowStyle = useMemo<RowStyle>(() => ({ backgroundColor: '#f8f9fa' }), []);

  useEffect(() => {
    setRowsSnapshot((previousRows) =>
      areMeasurementRowsEqual(previousRows, normalizedInitialMeasurements)
        ? previousRows
        : normalizedInitialMeasurements
    );
  }, [normalizedInitialMeasurements]);

  /**
   * Reads the current table rows from the grid API.
   */
  const collectGridRows = useCallback((api = ref.current?.api): TableMeasurement<TMeasurement>[] => {
    const model = api?.getModel();
    const nRows = model?.getRowCount() ?? 0;
    const rows: TableMeasurement<TMeasurement>[] = [];

    for (let i = 0; i < nRows; i++) {
      const row = model?.getRow(i)?.data;

      if (row) {
        rows.push(normalizeMeasurementRowLockMetadata(row));
      }
    }

    return rows;
  }, []);

  /**
   * Synchronizes the local row snapshot with the current grid contents.
   */
  const syncRowsFromGrid = useCallback(
    (api = ref.current?.api) => {
      const nextRows = collectGridRows(api);

      setRowsSnapshot((previousRows) => (areMeasurementRowsEqual(previousRows, nextRows) ? previousRows : nextRows));
    },
    [collectGridRows]
  );

  /**
   * Replaces a single row in the local snapshot without rebuilding the full table state.
   */
  const replaceRowInSnapshot = useCallback((nextRow: TableMeasurement<TMeasurement>) => {
    setRowsSnapshot((previousRows) => {
      const nextRows = previousRows.map((row) => (row._id === nextRow._id ? nextRow : row));

      return areMeasurementRowsEqual(previousRows, nextRows) ? previousRows : nextRows;
    });
  }, []);

  /**
   * Handles the event when a row is deleted.
   */
  const onMeasurementRowDelete = useCallback(
    (index: number): void => {
      ref.current?.api.applyTransaction({
        remove: [ref.current?.api.getModel().getRow(index)?.data],
      });
      syncRowsFromGrid();
    },
    [syncRowsFromGrid]
  );

  /**
   * Confirms the deletion of a row.
   */
  const confirmRowDelete = useCallback(
    (index: number): void => {
      const row = ref.current?.api.getModel().getRow(index)?.data;

      if (lockingEnabled && row?._locked) {
        /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
        return;
      }

      confirm({
        title: 'Zmazanie merania',
        content: 'Naozaj chcete zmazať toto meranie?',
        onConfirm: () => onMeasurementRowDelete(index),
      });
    },
    [confirm, lockingEnabled, onMeasurementRowDelete]
  );

  /**
   * Applies a confirmed lock state change to a single measurement row.
   */
  const applyMeasurementRowLockToggle = useCallback(
    (index: number, locked: boolean): void => {
      const rowNode = ref.current?.api.getModel().getRow(index);
      const row = rowNode?.data;

      if (!rowNode || !row) {
        /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
        return;
      }

      const nextRow = setMeasurementRowLocked(row, locked);

      rowNode.setData(nextRow);
      replaceRowInSnapshot(nextRow);

      if (locked) {
        rowNode.setSelected(false);
        setSelectedRows((previousRows) => previousRows.filter((selectedRow) => selectedRow._id !== nextRow._id));
      }

      ref.current?.api.refreshCells({
        rowNodes: [rowNode],
        force: true,
      });
      ref.current?.api.redrawRows({ rowNodes: [rowNode] });
    },
    [replaceRowInSnapshot]
  );

  /**
   * Confirms and updates the lock state of a single measurement row.
   */
  const onMeasurementRowLockToggle = useCallback(
    (index: number, locked: boolean): void => {
      confirm({
        title: locked ? 'Uzamknutie merania' : 'Odomknutie merania',
        content: locked
          ? 'Naozaj chcete uzamknúť toto meranie? Uzamknuté meranie nebude možné upravovať ani zmazať.'
          : 'Naozaj chcete odomknúť toto meranie? Po odomknutí bude možné meranie znovu upravovať.',
        onConfirm: () => applyMeasurementRowLockToggle(index, locked),
      });
    },
    [applyMeasurementRowLockToggle, confirm]
  );

  /**
   * Adds a new measurement to the table.
   */
  const handleAddMeasurement = useCallback((): void => {
    ref.current?.api.applyTransaction({
      add: [
        resetMeasurementRowLockMetadata({
          _id: nanoid(),
          _faults: [],
          ...(spec.reduce((acc, spec) => ({ ...acc, [spec.name]: '' }), {}) as any),
        }),
      ],
      addIndex: ref.current.api.getModel().getRowCount(),
    });
    syncRowsFromGrid();
  }, [spec, syncRowsFromGrid]);

  const handleGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      api.sizeColumnsToFit();
      syncRowsFromGrid(api);
    },
    [syncRowsFromGrid]
  );

  const handleSelectionChanged = useCallback(({ api }: SelectionChangedEvent) => {
    setSelectedRows(api.getSelectedRows());
  }, []);

  const handleRowDataUpdated = useCallback(
    ({ api }: RowDataUpdatedEvent) => {
      syncRowsFromGrid(api);
    },
    [syncRowsFromGrid]
  );

  const getRowStyle = useCallback(
    ({ data }: { data?: TableMeasurement<TMeasurement> }) =>
      (lockingEnabled || isViewMode) && data?._locked ? lockedRowStyle : undefined,
    [isViewMode, lockedRowStyle, lockingEnabled]
  );

  const noRowsOverlayComponent = useCallback(() => 'Nenájdené žiadne záznamy', []);

  const columns: ColDef[] = useMemo(() => {
    const cols: ColDef[] = isViewMode
      ? []
      : [
          {
            field: 'checkbox',
            headerName: '',
            checkboxSelection: ({ data }) => !lockingEnabled || !data?._locked,
            headerCheckboxSelection: true,
            suppressMovable: true,
            width: 36,
            minWidth: 36,
            maxWidth: 36,
          },
          {
            field: '_drag',
            headerName: '',
            rowDrag: ({ data }) => !lockingEnabled || !data?._locked,
            width: 32,
            minWidth: 32,
            maxWidth: 32,
          },
          {
            field: '_id',
            headerName: 'ID merania',
            hide: true,
          },
        ];

    spec.forEach((spec, i) => {
      cols.push(defineColumn({ spec, index: i, lockingEnabled, editable: !isViewMode }));
    });

    if (!isViewMode) {
      cols.push({
        field: '_faults',
        headerName: 'Nedostatky',
        hide: true,
        valueGetter: ({ data }) => JSON.stringify(data._faults),
      });
    }

    if (isViewMode && lockingEnabled) {
      cols.push(
        {
          field: '_lockStatus',
          headerName: 'Stav uzamknutia',
          minWidth: 160,
          valueGetter: ({ data }) => (data ? getMeasurementLockStatusLabel(data) : ''),
          cellRenderer: ({ data, value }: { data?: TableMeasurement<TMeasurement>; value: string }) => {
            if (!data || !data._locked) {
              return value;
            }

            return (
              <Group spacing={6} noWrap>
                <IconLock size={16} stroke={1.5} />
                <span>{value}</span>
              </Group>
            );
          },
        },
        {
          field: '_lockUpdatedAt',
          headerName: 'Posledná zmena uzamknutia',
          minWidth: 220,
          valueGetter: ({ data }) => (data ? getMeasurementLastLockUpdateLabel(data) : ''),
          cellRenderer: ({ data, value }: { data: TableMeasurement<TMeasurement>; value: string }) => {
            const historyLines = data ? getMeasurementLockHistoryLines(data) : [];

            return (
              <Group spacing={6} noWrap>
                <span>{value}</span>
                {historyLines.length > 0 && (
                  <span aria-label="História uzamknutia">
                    <QuestionMarkTooltip
                      withinPortal
                      multiline
                      openDelay={100}
                      label={
                        <Stack spacing={4}>
                          {historyLines.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </Stack>
                      }
                    />
                  </span>
                )}
              </Group>
            );
          },
        }
      );
    } else if (!isViewMode) {
      cols.push({
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: lockingEnabled ? 228 : 180,
        minWidth: lockingEnabled ? 228 : 180,
        maxWidth: lockingEnabled ? 228 : 180,
        cellRenderer: ({
          node,
          data,
          context,
        }: {
          node: IRowNode<TableMeasurement<TMeasurement>>;
          data: TableMeasurement<TMeasurement>;
          context: { deviceTypeId: number };
        }) =>
          ActionsColumn({
            node,
            hideFaults,
            context,
            lockingEnabled,
            onDelete: confirmRowDelete,
            onToggleLock: onMeasurementRowLockToggle,
            syncRows: syncRowsFromGrid,
          }),
      });
    }

    return cols;
  }, [confirmRowDelete, hideFaults, isViewMode, lockingEnabled, onMeasurementRowLockToggle, spec, syncRowsFromGrid]);

  const columnsWithBulk = useMemo(
    () =>
      isViewMode
        ? columns
        : [
            {
              field: '_bulk',
              headerName: '',
              suppressMovable: true,
              width: 0,
              minWidth: 0,
              maxWidth: 0,
              colSpan: ({ node }) => (node?.isRowPinned() ? 999 : 0),
            } satisfies ColDef,
            ...columns,
          ],
    [columns, isViewMode]
  );

  const tableFooter = useMemo(
    () => (
      <Group spacing={16}>
        <ButtonWithConfirm
          variant="tertiary"
          leftIcon={<IconX stroke="1.5" height={24} width={24} />}
          size="md"
          loaderPosition="center"
          loaderProps={{ size: 0 }}
          onConfirm={onClose}
          confirmTitle="Zrušiť zmeny v meraniach"
          confirmMessage="Naozaj chcete zrušiť zmeny v meraniach? Všetky neuložené zmeny budú stratené."
        >
          Zrušiť
        </ButtonWithConfirm>
        <Button
          variant="primary"
          leftIcon={<IconCheck stroke="1.5" height={24} width={24} />}
          size="md"
          onClick={() => {
            const nRows = ref.current?.api.getModel().getRowCount() ?? 0;
            const data = [];

            for (let i = 0; i < nRows; i++) {
              const row = ref.current?.api.getModel().getRow(i)?.data;

              for (const requiredColumn of requiredColumns) {
                if (!row[requiredColumn]) {
                  showNotification({
                    title: 'Chýbajúce údaje!',
                    message: 'Niektoré povinné údaje neboli vyplnené.',
                    color: ERROR_NOTIFICATION_COLOR,
                  });
                  return;
                }
              }

              data.push(normalizeMeasurementRowLockMetadata(row));
            }

            ref.current?.api.stopEditing();

            onSubmit({ measurements: data as TableMeasurement<TMeasurement>[] });
          }}
        >
          Uložiť
        </Button>
      </Group>
    ),
    [onClose, onSubmit, requiredColumns]
  );

  const viewModeFooter = useMemo(
    () => (
      <Group position="right">
        <Button variant="tertiary" leftIcon={<IconX stroke="1.5" height={24} width={24} />} size="md" onClick={onClose}>
          Zavrieť
        </Button>
      </Group>
    ),
    [onClose]
  );

  const hasBulkActions = useMemo(() => selectedRows.length > 0, [selectedRows]);
  const hasLockedRows = useMemo(() => rowsSnapshot.some((row) => row._locked), [rowsSnapshot]);

  const dataTypeDefinitions: { [cellDataType: string]: DataTypeDefinition<TableMeasurement<TMeasurement>> } =
    useMemo(() => {
      return {
        number: {
          baseDataType: 'number',
          extendsDataType: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined) {
              return '';
            }

            return String(params.value);
          },
        },
        date: {
          baseDataType: 'date',
          extendsDataType: 'date',
          // display the date in the format of 'dd.MM.yyyy'
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || String(params.value) === '') {
              return '';
            }

            return new Date(params.value).toLocaleDateString('sk-SK');
          },
        },
        select: {
          baseDataType: 'text',
          extendsDataType: 'text',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || String(params.value) === '') {
              return '';
            }

            return params.colDef.cellEditorParams?.options?.find(
              (o: { value: string; label: string }) => o.value === params.value
            )?.label;
          },
        },
      };
    }, []);

  /**
   * Copies the selected rows to the end of the table.
   */
  const bulkActionCopyAtEnd = useCallback(() => {
    const selected = ref.current?.api.getSelectedRows() || [];

    ref.current?.api.applyTransaction({
      add: selected.map((row) => ({
        ...resetMeasurementRowLockMetadata(row),
        _id: nanoid(),
      })),
      addIndex: ref.current.api.getModel().getRowCount(),
    });

    ref.current?.api.deselectAll();
    syncRowsFromGrid();
  }, [syncRowsFromGrid]);

  /**
   * Copies the selected rows after the index of row that was selected last.
   * We have rows on indexes 0,1,2, the user first selects row with index 0, then index 2
   * and lastly with index 1, the rows will be copied after the row with index 1.
   */
  const bulkActionCopyAtIndex = useCallback(() => {
    const selected = ref.current?.api.getSelectedRows() || [];

    if (selected.length === 0) {
      return;
    }

    const lastSelected = selected[selected.length - 1];
    const lastSelectedIndex = ref.current?.api.getModel().getRowNode(lastSelected._id)?.rowIndex;
    const destinationIndex = lastSelectedIndex === null || lastSelectedIndex === undefined ? 0 : lastSelectedIndex + 1;

    ref.current?.api.applyTransaction({
      add: selected.map((row) => ({
        ...resetMeasurementRowLockMetadata(row),
        _id: nanoid(),
      })),
      addIndex: destinationIndex,
    });

    ref.current?.api.deselectAll();
    syncRowsFromGrid();
  }, [syncRowsFromGrid]);

  /**
   * Deletes the selected rows from the table.
   */
  const bulkActionDelete = useCallback(() => {
    const selected = ref.current?.api.getSelectedRows() || [];

    confirm({
      title: `Zmazať merania (${selected.length})`,
      content: 'Naozaj si prajete zmazať tieto merania? Akcia je nevratná.',
      onConfirm: () => {
        ref.current?.api.applyTransaction({
          remove: selected,
        });

        ref.current?.api.deselectAll();
        syncRowsFromGrid();
      },
    });
  }, [confirm, syncRowsFromGrid]);

  const bulkActions = useMemo(() => {
    return (
      <Group spacing={12}>
        <Button variant="primary" leftIcon={<IconCopy />} onClick={bulkActionCopyAtEnd}>
          Kopírovať označené na koniec
        </Button>
        <Button variant="primary" leftIcon={<IconCopy />} onClick={bulkActionCopyAtIndex}>
          Kopírovať označené pod
        </Button>
        <Button variant="primary" leftIcon={<IconTrashX />} onClick={bulkActionDelete}>
          Zmazať označené
        </Button>
      </Group>
    );
  }, [bulkActionCopyAtEnd, bulkActionCopyAtIndex, bulkActionDelete]);

  /**
   * Exports the displayed measurements as a typed XLSX workbook.
   */
  const exportFile = useCallback(
    async (params: CsvExportParams) => {
      const api = ref.current?.api;

      if (!api) {
        /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
        return;
      }

      api.stopEditing();

      const rows: TableMeasurement<TMeasurement>[] = [];
      api.forEachNodeAfterFilterAndSort(({ data }) => {
        if (data) {
          rows.push(data);
        }
      });
      const columnKeys = (params.columnKeys ?? []).filter((key): key is string => typeof key === 'string');
      const workbook = buildMeasurementWorkbook({ rows, spec, columnKeys });

      writeFile(workbook, params.fileName ?? 'export.xlsx', { type: 'file', bookType: 'xlsx' });
    },
    [spec]
  );

  const displayImportHint = useCallback(
    () =>
      acknowledge({
        title: 'Import meraní',
        content: <ImportMeasurementsHint revisionKind={revisionKind} />,
        zIndex: 300,
      }),
    [acknowledge, revisionKind]
  );

  const handleImport: ImportMeasurementsCallback<TMeasurement> = useCallback(
    async (file) => {
      if (lockingEnabled && hasLockedRows) {
        throw new Error('Import nie je dostupný, kým tabuľka obsahuje uzamknuté merania.');
      }

      if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        throw new Error('Súbor musí byť vo formáte XLSX.');
      }

      const contents = await file.arrayBuffer();
      const workbook = readMeasurementWorkbook(contents);
      const rows: TableMeasurement<TMeasurement>[] = parseMeasurementWorkbook<TMeasurement>({
        workbook,
        spec,
        createRowId: nanoid,
      });

      ref.current?.api.setRowData(rows);
      syncRowsFromGrid();

      return rows;
    },
    [hasLockedRows, lockingEnabled, spec, syncRowsFromGrid]
  );

  const tableActions = useMemo(
    () => (
      <Group spacing={16}>
        <Button
          variant="primary"
          onClick={handleAddMeasurement}
          size="md"
          leftIcon={<IconPlus stroke="1.5" size={24} />}
        >
          Pridať meranie
        </Button>
        <Button
          variant="tertiary"
          size="md"
          leftIcon={<IconFileExport stroke="1.5" size={24} />}
          onClick={startExporting}
        >
          Exportovať
        </Button>
        <ExportModal
          agGridRef={ref}
          columns={columns}
          dataExport={{
            modalTitle: 'Exportovať merania',
            columnKeys: [...spec.map(({ name }) => name), '_faults'],
            fileName: 'merania.xlsx',
          }}
          opened={exporting}
          onClose={stopExporting}
          onExport={(params) => {
            exportFile(params);
          }}
        />
        <Button
          variant="tertiary"
          size="md"
          leftIcon={<IconTableImport stroke="1.5" size={24} />}
          onClick={startImporting}
          disabled={lockingEnabled && hasLockedRows}
        >
          Importovať
        </Button>
        <ImportMeasurementsModal
          title="Importovať merania"
          opened={importing}
          onClose={stopImporting}
          onImport={handleImport}
          onHint={displayImportHint}
        />
      </Group>
    ),
    [
      columns,
      displayImportHint,
      exporting,
      exportFile,
      handleImport,
      hasLockedRows,
      importing,
      lockingEnabled,
      spec,
      startExporting,
      startImporting,
      stopExporting,
      stopImporting,
    ]
  );

  // Set quick filter.
  useEffect(() => {
    ref.current?.api?.setQuickFilter(search);
  }, [search]);

  return (
    <Stack h="100%" spacing={16} ref={wrapperRef}>
      <TableWrapper>
        <AgGridReact
          ref={ref}
          getRowId={({ data }) => data._id}
          rowData={rowsSnapshot}
          columnDefs={columnsWithBulk}
          dataTypeDefinitions={dataTypeDefinitions}
          autoSizePadding={8}
          // drag and drop properties
          rowDragManaged={!isViewMode}
          rowDragMultiRow={!isViewMode}
          rowSelection={isViewMode ? undefined : 'multiple'}
          isRowSelectable={({ data }) => !lockingEnabled || !data?._locked}
          suppressMoveWhenRowDragging
          context={gridContext}
          onGridReady={handleGridReady}
          onSelectionChanged={handleSelectionChanged}
          onRowDataUpdated={handleRowDataUpdated}
          headerHeight={60}
          rowHeight={52}
          className="ag-theme-alpine measurements-table"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          suppressColumnVirtualisation
          getRowStyle={getRowStyle}
          noRowsOverlayComponent={noRowsOverlayComponent}
          pinnedTopRowData={!isViewMode && hasBulkActions && rowsSnapshot.length > 0 ? [rowsSnapshot[0]] : []}
        />
        {!isViewMode && (
          <Flex gap={12} p={16} align="center" h="100%" bg="gray.0" justify="space-between">
            {tableActions}
            {tableFooter}
          </Flex>
        )}
        {isViewMode && (
          <Flex gap={12} p={16} align="center" h="100%" bg="gray.0" justify="flex-end">
            {viewModeFooter}
          </Flex>
        )}
        {!isViewMode && hasBulkActions && (
          <Group
            position="apart"
            pos="absolute"
            top={122}
            left={0}
            w="100%"
            h={73}
            bg="red.6"
            px={24}
            py={16}
            sx={{
              zIndex: 200,
              borderTop: '2px solid white',
              borderLeft: '2px solid white',
              borderRight: '2px solid white',
            }}
          >
            {bulkActions}
            <Group spacing={8}>
              <P1Medium color="white">vybrané: {selectedRows.length}</P1Medium>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<IconX strokeWidth={1.5} size={16} />}
                onClick={() => ref.current?.api.deselectAll()}
              >
                Odznačiť všetko
              </Button>
            </Group>
          </Group>
        )}
      </TableWrapper>
    </Stack>
  );
}
